import { useEffect, useState } from 'react';
import { callLog } from '../data/mock';
import { usePersonLookup } from '../data/people';
import { Icon } from '../components/Icon';
import { Avatar, IconButton, NavBar, OrgLogo, PersonAvatar, cx, initials } from '../components/ui';
import {
  endCall,
  incomingCall,
  startCall,
  updateCall,
  useStore,
  type CallState,
} from '../state/store';
import { useActiveTheme } from '../state/theme';

export function CallsScreen() {
  const lookup = usePersonLookup();
  const hasOrg = useStore((s) => s.orgId !== null);
  const theme = useActiveTheme();
  const log = hasOrg ? callLog : callLog.filter(() => false);
  return (
    <div className="screen">
      <NavBar
        large
        title={
          <span className="title-with-logo">
            {theme.orgName && <OrgLogo size={28} />}
            Calls
          </span>
        }
        trailing={<IconButton icon="addPerson" label="New call" />}
      />
      <div className="screen-scroll">
        <div className="call-quick">
          <button
            type="button"
            className="call-quick-item"
            onClick={() => startCall(['jonas'], false)}
          >
            <span className="call-quick-icon">
              <Icon name="phone" size={22} />
            </span>
            Call
          </button>
          <button
            type="button"
            className="call-quick-item"
            onClick={() => startCall(['jonas'], true)}
          >
            <span className="call-quick-icon">
              <Icon name="video" size={22} />
            </span>
            Video
          </button>
          <button type="button" className="call-quick-item" onClick={incomingCall}>
            <span className="call-quick-icon">
              <Icon name="bell" size={22} />
            </span>
            Ring me
          </button>
        </div>
        <h2 className="section-title inset">Recent</h2>
        <div className="section-body flush">
          {log.length === 0 && <p className="screen-intro">No calls yet.</p>}
          {log.map((c) => {
            const first = lookup(c.with[0] ?? 'me');
            const live = c.duration === 'Live now';
            return (
              <div key={c.id} className="list-row call-row">
                {c.groupName ? (
                  <Avatar name={c.groupName} hue="orange" size={44} group />
                ) : (
                  <PersonAvatar id={first.id} size={44} />
                )}
                <span className="list-row-text">
                  <span className={cx('list-row-title', c.direction === 'missed' && 'is-missed')}>
                    {c.groupName ?? first.name}
                  </span>
                  <span className="list-row-subtitle call-meta">
                    <Icon
                      name={
                        c.direction === 'missed'
                          ? 'phoneOff'
                          : c.direction === 'outgoing'
                            ? 'forward'
                            : 'reply'
                      }
                      size={14}
                    />
                    {c.direction === 'missed'
                      ? 'Missed'
                      : c.direction === 'outgoing'
                        ? 'Outgoing'
                        : 'Incoming'}{' '}
                    · {c.time}
                    {c.duration && !live && ` · ${c.duration}`}
                  </span>
                </span>
                {live ? (
                  <button
                    type="button"
                    className="button button-small button-success"
                    onClick={() => startCall(c.with, true, c.groupName)}
                  >
                    Join
                  </button>
                ) : (
                  <IconButton
                    icon={c.video ? 'video' : 'phone'}
                    label={c.video ? 'Video call' : 'Call'}
                    onClick={() => startCall(c.with, c.video, c.groupName)}
                  />
                )}
              </div>
            );
          })}
        </div>
        <p className="list-footnote">
          <Icon name="lock" size={13} /> Calls are end-to-end encrypted.
        </p>
      </div>
    </div>
  );
}

function useElapsed(since: number, active: boolean): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  const s = Math.max(0, Math.floor((now - since) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Incoming, outgoing and active calls. `presentation` follows the platform (spec section 7). */
export function CallView({
  call,
  presentation,
}: {
  call: CallState;
  presentation: 'fullscreen' | 'window';
}) {
  const lookup = usePersonLookup();
  const theme = useActiveTheme();
  const elapsed = useElapsed(call.startedAt, call.phase === 'active');
  const group = !!call.groupName;
  const first = lookup(call.with[0] ?? 'me');
  const title = call.groupName ?? first.name;

  if (call.phase === 'incoming') {
    return (
      <div className={cx('call', 'call-incoming', presentation === 'window' && 'is-window')}>
        <div
          className="call-backdrop"
          style={{ ['--hue' as string]: `var(--color-avatar-${first.avatar}-background)` }}
        />
        <div className="call-top">
          <span className="call-app">
            <Icon name="lock" size={12} /> Circles {call.video ? 'video' : 'audio'} · end-to-end
            encrypted
          </span>
          <PersonAvatar id={first.id} size={112} />
          <h2 className="call-name">{title}</h2>
          <p className="call-status">
            {first.title}
            {theme.orgName ? ` · ${theme.orgName}` : ''}
          </p>
        </div>
        <div className="call-incoming-actions">
          <div className="call-secondary">
            <button type="button" className="call-mini">
              <Icon name="chats" size={20} /> Message
            </button>
            <button type="button" className="call-mini">
              <Icon name="expiry" size={20} /> Remind me
            </button>
          </div>
          <div className="call-answer-row">
            <span className="call-answer">
              <button
                type="button"
                className="call-round decline"
                onClick={endCall}
                aria-label="Decline"
              >
                <Icon name="phoneOff" size={30} />
              </button>
              Decline
            </span>
            <span className="call-answer">
              <button
                type="button"
                className="call-round accept"
                onClick={() => updateCall({ phase: 'active', startedAt: Date.now() })}
                aria-label="Accept"
              >
                <Icon name={call.video ? 'video' : 'phone'} size={30} />
              </button>
              Accept
            </span>
          </div>
        </div>
      </div>
    );
  }

  const tiles = group ? ['me', ...call.with].slice(0, 8) : [];
  return (
    <div
      className={cx(
        'call',
        group ? 'call-group' : call.video ? 'call-video' : 'call-audio',
        presentation === 'window' && 'is-window',
      )}
    >
      {!group && call.video && !call.cameraOff && (
        <div className="call-remote-video">
          <VideoPlaceholder id={first.id} large />
        </div>
      )}
      {!group && (!call.video || call.cameraOff) && (
        <div
          className="call-backdrop"
          style={{ ['--hue' as string]: `var(--color-avatar-${first.avatar}-background)` }}
        />
      )}
      <header className="call-header">
        <span className="call-app">
          <Icon name="lock" size={12} /> End-to-end encrypted
        </span>
        <h2 className="call-name small">{title}</h2>
        <p className="call-status">
          {call.phase === 'outgoing'
            ? 'Ringing…'
            : group
              ? `${tiles.length} people · ${elapsed}`
              : elapsed}
        </p>
      </header>

      {!group && (!call.video || call.cameraOff) && (
        <div className="call-center">
          <PersonAvatar id={first.id} size={120} />
        </div>
      )}
      {!group && call.video && !call.cameraOff && (
        <div className="call-self-view">
          <VideoPlaceholder id="me" />
        </div>
      )}
      {group && (
        <div className={cx('call-grid', `n-${tiles.length}`)}>
          {tiles.map((pid, i) => {
            const p = lookup(pid);
            const speaking = i === 1;
            const muted = pid === 'me' ? call.muted : i === 3;
            const camOff = pid === 'me' ? call.cameraOff : i === 2;
            return (
              <div key={pid} className={cx('call-tile', speaking && 'is-speaking')}>
                {camOff ? (
                  <span className="tile-avatar">
                    <Avatar name={p.name} hue={p.avatar} size={64} />
                  </span>
                ) : (
                  <VideoPlaceholder id={pid} />
                )}
                <span className="tile-name">
                  {muted && <Icon name="microphoneOff" size={13} />}
                  {pid === 'me' ? 'You' : p.name.split(' ')[0]}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className="call-controls">
        <CallControl
          icon={call.speaker ? 'speaker' : 'speaker'}
          label="Speaker"
          active={call.speaker}
          onClick={() => updateCall({ speaker: !call.speaker })}
        />
        <CallControl
          icon={call.cameraOff ? 'videoOff' : 'video'}
          label="Camera"
          active={!call.cameraOff}
          onClick={() => updateCall({ cameraOff: !call.cameraOff, video: true })}
        />
        <CallControl
          icon={call.muted ? 'microphoneOff' : 'microphone'}
          label={call.muted ? 'Unmute' : 'Mute'}
          active={call.muted}
          onClick={() => updateCall({ muted: !call.muted })}
        />
        {(group || call.video) && (
          <CallControl icon="switchCamera" label="Flip" onClick={() => undefined} />
        )}
        {group && <CallControl icon="addPerson" label="Add" onClick={() => undefined} />}
        <button
          type="button"
          className="call-round decline small"
          onClick={endCall}
          aria-label="End call"
        >
          <Icon name="phoneOff" size={26} />
        </button>
      </div>
    </div>
  );
}

function CallControl({
  icon,
  label,
  active,
  onClick,
}: {
  icon: Parameters<typeof Icon>[0]['name'];
  label: string;
  active?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cx('call-control', active && 'is-active')}
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
    >
      <Icon name={icon} size={24} />
    </button>
  );
}

/** Stands in for a camera image: a soft gradient with the person's initials. */
function VideoPlaceholder({ id, large }: { id: string; large?: boolean }) {
  const lookup = usePersonLookup();
  const p = lookup(id);
  return (
    <div
      className={cx('video-placeholder', large && 'is-large')}
      style={{ ['--hue' as string]: `var(--color-avatar-${p.avatar}-foreground)` }}
    >
      <span className="video-figure">
        <span className="video-head">{initials(p.name)}</span>
        <span className="video-body" />
      </span>
    </div>
  );
}

/** Desktop: incoming calls ring as a native notification (spec section 7). */
export function IncomingCallToast({ call }: { call: CallState }) {
  const lookup = usePersonLookup();
  const p = lookup(call.with[0] ?? 'me');
  return (
    <div className="call-toast" role="alertdialog" aria-label={`Incoming call from ${p.name}`}>
      <PersonAvatar id={p.id} size={44} />
      <div className="call-toast-text">
        <strong>{p.name}</strong>
        <span>Incoming {call.video ? 'video' : 'voice'} call · Circles</span>
      </div>
      <button
        type="button"
        className="call-round decline tiny"
        onClick={endCall}
        aria-label="Decline"
      >
        <Icon name="phoneOff" size={18} />
      </button>
      <button
        type="button"
        className="call-round accept tiny"
        onClick={() => updateCall({ phase: 'active', startedAt: Date.now() })}
        aria-label="Accept"
      >
        <Icon name={call.video ? 'video' : 'phone'} size={18} />
      </button>
    </div>
  );
}

/** A minimised call keeps a bar at the top of the app. */
export function CallBar({ call }: { call: CallState }) {
  const lookup = usePersonLookup();
  const elapsed = useElapsed(call.startedAt, true);
  return (
    <button type="button" className="call-bar" onClick={() => updateCall({ minimized: false })}>
      <Icon name={call.video ? 'video' : 'phone'} size={15} />
      {call.groupName ?? lookup(call.with[0] ?? 'me').name} · {elapsed} · Tap to return
    </button>
  );
}
